import { useState } from "react";
import { Bell, Check, CheckCheck } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { t } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useNotificationsContext } from "@/contexts/NotificationsContext";
import {
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
  type NotificationPayload,
} from "@/hooks/useNotifications";
import { cn } from "@/lib/utils";

/**
 * Notifications bell + dropdown panel. Reads live state from
 * `NotificationsProvider` (REST inbox + socket deltas) and exposes
 * mark-one / mark-all-read. Only user-scoped rows (`userId != null`) carry
 * per-user read state in v1, so the unread affordance is gated on that —
 * matching the badge count maintained in the context/hook.
 */
export function NotificationsBell() {
  const { t: lt } = useLingui();
  const [open, setOpen] = useState(false);
  const { notifications, unreadCount, isLoading } = useNotificationsContext();
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();

  const badge = unreadCount > 99 ? "99+" : String(unreadCount);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="relative hidden rounded-lg border border-(--shell-border) bg-(--shell-control-bg) p-2 text-(--color-text-secondary) transition-colors hover:bg-(--color-surface-accent) sm:inline-flex"
          title={lt`Notifications`}
          aria-label={
            unreadCount > 0
              ? lt`Notifications (${unreadCount} unread)`
              : lt`Notifications`
          }
        >
          <Bell className="size-5" />
          {unreadCount > 0 && (
            <span className="absolute -right-1 -top-1 flex min-w-4 items-center justify-center rounded-full bg-(--color-danger) px-1 text-[10px] font-semibold leading-4 text-(--color-danger-fg)">
              {badge}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        data-testid="notifications-panel"
        className="w-[min(24rem,calc(100vw-1rem))] p-0"
      >
        <header className="flex items-center justify-between gap-2 border-b border-(--color-border) px-4 py-3">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-semibold text-(--color-text-primary)">
              {t`Notifications`}
            </h2>
            {unreadCount > 0 && (
              <span className="rounded-full bg-(--color-surface-muted) px-2 py-0.5 text-xs font-medium text-(--color-text-secondary)">
                {unreadCount}
              </span>
            )}
          </div>
          {unreadCount > 0 && (
            <button
              type="button"
              onClick={() => markAllRead.mutate()}
              disabled={markAllRead.isPending}
              className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-(--color-action) transition-colors hover:bg-(--color-surface-muted) disabled:opacity-50"
            >
              <CheckCheck className="size-3.5" />
              {t`Mark all read`}
            </button>
          )}
        </header>

        <div className="max-h-96 overflow-y-auto">
          {isLoading ? (
            <LoadingState />
          ) : notifications.length === 0 ? (
            <EmptyState />
          ) : (
            <ul className="divide-y divide-(--color-border)">
              {notifications.map((n) => (
                <NotificationRow
                  key={n.id}
                  notification={n}
                  onMarkRead={() => markRead.mutate(n.id)}
                />
              ))}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function isUnread(n: NotificationPayload): boolean {
  // Org-level rows have no per-user read state in v1; only user-scoped rows
  // count toward the badge, so only they get the unread affordance.
  return n.userId != null && n.readAt == null;
}

function NotificationRow({
  notification,
  onMarkRead,
}: {
  notification: NotificationPayload;
  onMarkRead: () => void;
}) {
  const { t: lt } = useLingui();
  const unread = isUnread(notification);
  const createdAt = new Date(notification.createdAt);
  const timeAgo = formatDistanceToNow(createdAt, { addSuffix: true });

  return (
    <li
      className={cn(
        "flex gap-3 px-4 py-3 transition-colors hover:bg-(--color-surface-muted)",
        unread && "bg-(--color-surface-accent)/40",
      )}
    >
      <span
        aria-hidden
        className={cn(
          "mt-1.5 size-2 shrink-0 rounded-full",
          unread ? "bg-(--color-action)" : "bg-transparent",
        )}
      />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-(--color-text-primary)">
          {notification.title}
        </p>
        {notification.body && (
          <p className="mt-0.5 text-sm text-(--color-text-secondary)">
            {notification.body}
          </p>
        )}
        <time
          dateTime={notification.createdAt}
          className="mt-1 block text-xs text-(--color-text-secondary)"
        >
          {timeAgo}
        </time>
      </div>
      {unread && (
        <button
          type="button"
          onClick={onMarkRead}
          title={lt`Mark as read`}
          aria-label={lt`Mark as read`}
          className="size-7 shrink-0 self-start rounded-md text-(--color-text-secondary) transition-colors hover:bg-(--color-surface-content) hover:text-(--color-text-primary) inline-flex items-center justify-center"
        >
          <Check className="size-4" />
        </button>
      )}
    </li>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-4 py-12 text-center">
      <div className="flex size-11 items-center justify-center rounded-full bg-(--color-surface-muted)">
        <Bell className="size-5 text-(--color-text-secondary)" />
      </div>
      <p className="text-sm font-medium text-(--color-text-primary)">
        {t`You're all caught up`}
      </p>
      <p className="text-sm text-(--color-text-secondary)">
        {t`New notifications will appear here.`}
      </p>
    </div>
  );
}

function LoadingState() {
  return (
    <ul className="divide-y divide-(--color-border)">
      {Array.from({ length: 4 }).map((_, i) => (
        <li key={i} className="flex gap-3 px-4 py-3">
          <span className="mt-1.5 size-2 shrink-0 rounded-full bg-(--color-surface-muted)" />
          <div className="flex-1 space-y-2">
            <div className="h-4 w-1/2 animate-pulse rounded bg-(--color-surface-muted)" />
            <div className="h-3 w-3/4 animate-pulse rounded bg-(--color-surface-muted)" />
            <div className="h-3 w-16 animate-pulse rounded bg-(--color-surface-muted)" />
          </div>
        </li>
      ))}
    </ul>
  );
}
